import RtrButton from "components/design-system/rtr-button";
import { isActiveMembership, isChurnedSubscriber, isSubscriptionMember } from "helpers/membership-helpers";
import { useDispatch, useSelector } from "react-redux";
import { navigateTo } from "helpers/location-helpers";
import { MODAL_NAMES } from "constants/modalNames";
import { calculateEligiblityToShowUrgencySignalOnUniversalPdp } from "../urgency-signal/wrapper";
import HeapHelpers from "helpers/heap-helpers";
import Routes from "routes";
import { displayModal } from "actions/shared-actions";
import SMBagActions from "actions/sm-bag-actions";
import PropTypes from "prop-types";
import { useSubscriptionRejoinUrl } from "hooks/use-subscription-rejoin-url";
import { openNotifyMeModal } from "slices/notifyMe-slice";
import NotifyBellWhite from "images/notify_bell_white.svg";

const generateAddToBagLabel = (isProspect, membershipError, styleUnavailable, canRenderNotifyMe, membershipState) => {
  if (styleUnavailable || membershipError) {
    return "Unavailable";
  } else if (isProspect && isChurnedSubscriber(membershipState)) {
    return "Reactivate Subscription";
  } else if (isProspect) {
    return "Add to Shipment";
  } else if (canRenderNotifyMe) {
    return (
      <>
        <NotifyBellWhite /> notify me
      </>
    );
  } else {
    return "Add to Bag";
  }
};

const getAddToBagFn = (
  isProspect,
  canRenderNotifyMe,
  onProspectUserAddToBag,
  onSubscriptionAddToBag,
  openNotifyMeModal
) => {
  if (isProspect) {
    return onProspectUserAddToBag;
  } else if (canRenderNotifyMe) {
    return openNotifyMeModal;
  } else {
    return onSubscriptionAddToBag;
  }
};

function isButtonDisabled(selectedSize, styleUnavailable, membershipError) {
  return !selectedSize || styleUnavailable || membershipError;
}

function isButtonLoading(smBagLoading) {
  return smBagLoading;
}

export default function AddToBag({ availability, availabilityLoaded, membershipError, selectedSize }) {
  const smBagLoading = useSelector(state => state.smBag?.smBagLoading);
  const membershipState = useSelector(state => state.membershipState);
  const userData = useSelector(state => state.userData);
  const addresses = userData?.userProfile?.addresses;
  const staticProduct = useSelector(state => state.staticProduct);
  const dispatch = useDispatch();
  const isProspect = !isActiveMembership(membershipState);
  const { userCanRejoin, rejoinUrl } = useSubscriptionRejoinUrl();

  const selectedSizeAvailability =
    availabilityLoaded && availability?.find(availability => availability?.sku === selectedSize?.sku);

  // Assume available until availability has loaded
  const styleUnavailable = availabilityLoaded && !availability?.find(el => el.availableCount > 0);
  const selectSizeIsUnavailable =
    selectedSize && (!selectedSizeAvailability || !selectedSizeAvailability.availableCount);

  const canRenderNotifyMe =
    isSubscriptionMember(userData) &&
    availabilityLoaded &&
    (styleUnavailable || selectSizeIsUnavailable) &&
    !membershipError;

  // I am duplicating the logic from `membership-item-dummy-atb-container` which Stephen built
  // So that once we get rid of the old PDP - we can delete the component directly
  const onProspectUserAddToBag = e => {
    e.preventDefault();
    e.stopPropagation();

    const { isEligible, availabilityOfSelectedSku } = calculateEligiblityToShowUrgencySignalOnUniversalPdp(
      availability, // @TODO: Ensure this changes as intent/lens/mode changes
      selectedSize
    );

    HeapHelpers.fireHeapEvent("non-subscriber-membership-atb", {
      selectedSize,
      urgencySignalShown: isEligible,
      urgencySignalItemsLeft: availabilityOfSelectedSku,
      universalPdp: true,
    });

    if (userCanRejoin) {
      navigateTo(rejoinUrl);
    } else if (isChurnedSubscriber(membershipState)) {
      navigateTo(Routes.LP.plans);
    } else {
      dispatch(displayModal(MODAL_NAMES.PLANS_DRAWER));
    }
  };

  const onSubscriptionAddToBag = () => {
    const defaultAddress = addresses?.find(address => address?.default === true);

    const handleAddToMembershipBagSuccess = () => {
      // log the success
    };
    const handleAddToMembershipBagFailure = () => {
      // log the error
    };

    const addSubscriptionItemToBag = SMBagActions.addBagItemPromise(
      {
        sku: selectedSize?.sku,
        type: "members",
        zipCode: defaultAddress?.postalCode,
      },
      handleAddToMembershipBagSuccess,
      handleAddToMembershipBagFailure,
      { productId: staticProduct.productId, retailPriceInteger: staticProduct.msrp }
    );
    dispatch(addSubscriptionItemToBag);
  };

  const handleNotifyMeOpen = e => {
    e.preventDefault();
    dispatch(openNotifyMeModal(selectedSize?.sku));
  };

  return (
    <RtrButton
      style="primary"
      onClick={getAddToBagFn(
        isProspect,
        canRenderNotifyMe,
        onProspectUserAddToBag,
        onSubscriptionAddToBag,
        handleNotifyMeOpen
      )}
      dataTestId="universal-atb"
      dataHeapId="universal-atb"
      className="mt-8 mb-4"
      // FIXME: when implementing intents ensure -> !membershipState?.inventoryEligibilities?.length > 0 is added to loading state check to verify if membershipState inventory eligibilites has loaded
      loading={isButtonLoading(smBagLoading)}
      disabled={isButtonDisabled(selectedSize, styleUnavailable, membershipError)}
      fullWidth>
      {generateAddToBagLabel(isProspect, membershipError, styleUnavailable, canRenderNotifyMe, membershipState)}
    </RtrButton>
  );
}

AddToBag.propTypes = {
  availability: PropTypes.array,
  selectedSize: PropTypes.object,
  availabilityLoaded: PropTypes.bool,
  membershipError: PropTypes.object,
};
